import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfCtaText, sfCtaUrl, sfHeadline, sfImage, sfSub } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

// Colour pairs keyed by variant name: [textSideBg, accentColour]
const VARIANT_COLORS: Record<string, [string, string]> = {
  'teal-orange':  ['var(--brand-primary, #0d9488)', 'var(--brand-accent, #f97316)'],
  'blue-orange':  ['var(--brand-primary, #1d4ed8)', 'var(--brand-accent, #f97316)'],
  'dark':         ['#0f172a', '#38bdf8'],
  'light':        ['#f1f5f9', 'var(--brand-primary, #2563eb)'],
};

const HeroDiagonal: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const variant = c.variant || section.variant || '';
  const direction = c.angle_direction || c.direction || 'right';
  const isRight = direction !== 'left';

  // Resolve the text-side background colour from variant or custom control
  const [defaultBg, defaultAccent] = VARIANT_COLORS[variant] || ['var(--brand-primary, #1a365d)', 'var(--brand-accent, #f97316)'];
  const textBg = c.background_color || defaultBg;

  const textSide = (
    <div style={{
      flex: '1 1 50%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '80px 48px',
      position: 'relative',
      zIndex: 1,
      background: textBg,
      color: '#fff',
      clipPath: isRight
        ? 'polygon(0 0, 92% 0, 100% 100%, 0 100%)'
        : 'polygon(0 0, 100% 0, 100% 100%, 8% 100%)',
    }}>
      {c.badge_text && (
        <span style={{
          display: 'inline-block',
          background: 'rgba(255,255,255,0.2)',
          padding: '5px 14px',
          borderRadius: 9999,
          fontSize: '0.8rem',
          fontWeight: 600,
          marginBottom: 16,
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.25)',
          alignSelf: 'flex-start',
        }}>
          {c.badge_text}
        </span>
      )}
      <InlineEditText
        tag="h1"
        controlKey="headline"
        style={{ fontSize: '3rem', lineHeight: 1.15, fontWeight: 800, marginBottom: 20, letterSpacing: '-0.025em', color: '#fff' }}
      >
        {sfHeadline(c, 'Diagonal Hero Section')}
      </InlineEditText>
      <InlineEditText
        tag="p"
        controlKey="subheadline"
        style={{ fontSize: '1.125rem', color: 'rgba(255,255,255,0.85)', marginBottom: 36, maxWidth: 440, lineHeight: 1.7 }}
      >
        {sfSub(c, 'Your subheadline goes here with a compelling message.')}
      </InlineEditText>
      {/* Primary CTA: white background with brand-accent text (inverted — matches Python) */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <a
          href={sfCtaUrl(c)}
          className="sb-cta-btn"
          style={{
            display: 'inline-block',
            alignSelf: 'flex-start',
            padding: '14px 36px',
            borderRadius: 'var(--brand-btn-radius, 6px)',
            background: '#fff',
            color: textBg,
            fontWeight: 700,
            textDecoration: 'none',
            fontSize: '1rem',
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            transition: 'all 0.2s',
          }}
        >
          <InlineEditText tag="span" controlKey="cta_text">
            {sfCtaText(c, 'Learn More')}
          </InlineEditText>
        </a>
        {/* Optional secondary CTA */}
        {(c.cta_secondary_text || c.cta2_text || c.cta_secondary_label) && (
          <a
            href={c.cta_secondary_url || c.cta2_url || '#'}
            style={{
              display: 'inline-block',
              padding: '14px 36px',
              borderRadius: 'var(--brand-btn-radius, 6px)',
              background: 'transparent',
              color: '#fff',
              fontWeight: 700,
              textDecoration: 'none',
              fontSize: '1rem',
              border: '2px solid rgba(255,255,255,0.6)',
              transition: 'all 0.2s',
            }}
          >
            {c.cta_secondary_text || c.cta2_text || c.cta_secondary_label}
          </a>
        )}
      </div>
    </div>
  );

  const imageSide = (
    <div style={{
      flex: '1 1 50%',
      position: 'relative',
      minHeight: 480,
      overflow: 'hidden',
    }}>
      {sfImage(c) ? (
        <img
          src={sfImage(c)}
          alt={c.image_alt || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
        />
      ) : (
        <div style={{
          width: '100%', height: '100%',
          background: `linear-gradient(135deg, ${defaultAccent} 0%, ${textBg} 100%)`,
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'rgba(255,255,255,0.3)', fontSize: '4rem',
        }}>
          🖼
        </div>
      )}
    </div>
  );

  return (
    <header
      className="sb-render sb-render-hero-diagonal"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 540,
        overflow: 'hidden',
        background: 'var(--brand-bg, #fff)',
      }}
    >
      {isRight ? (
        <>{textSide}{imageSide}</>
      ) : (
        <>{imageSide}{textSide}</>
      )}
    </header>
  );
};

export default HeroDiagonal;
